import React, { useEffect, useMemo, useState } from "react";
import ".././components/DetallesObra.css";
import "./DetallesContenedor.css";
import { FiCalendar } from "react-icons/fi";
import { useHistory, useLocation } from "react-router-dom";
import Header from "./Header";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { setAlbaranPendiente, setCamiones, setTipos } from "../redux/actions";
import {
  fetchCamiones,
  fetchClientesContenedor,
  fetchTipos,
} from "../services/albaranes";
import { completarDiContenedor } from "../pluggins/albaranes";
import { useConnectionStatus } from "../hooks/useConnectionStatus";

const SERVICIOS = [
  { value: "llevada", label: "Llevada" },
  { value: "cambio", label: "Cambio" },
  { value: "retirada", label: "Retirada" },
];
const CAPACIDADES_PRESET = [6, 8, 12, 30];

function permitidoContenedor(valor) {
  return valor === true || valor === 1 || valor === "1";
}

// Última respuesta de clientes/direcciones, para poder crear el DI sin conexión.
const cacheKey = (conductorId) => `contenedores_clientes_${conductorId}`;

const DetallesContenedor = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const isOnline = useConnectionStatus();
  const user = useSelector((state) => state?.auth?.currentUser?.data);
  const camiones = useSelector((state) =>
    Array.isArray(state?.app?.camiones) ? state.app.camiones : [],
  );
  const tipos = useSelector((state) =>
    Array.isArray(state?.app?.tipos) ? state.app.tipos : [],
  );
  const albaranPendiente = useSelector((state) => state?.app?.albaranPendiente);
  const albaranEdicion = location?.albaran || null;
  const esEdicion = !!albaranEdicion?.id;

  const [clientes, setClientes] = useState([]);
  const [vertederos, setVertederos] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [clienteId, setClienteId] = useState("");
  const [direccionId, setDireccionId] = useState("");
  const [direccionLibre, setDireccionLibre] = useState("");
  const [servicio, setServicio] = useState("");
  const [capacidad, setCapacidad] = useState(CAPACIDADES_PRESET[0]);
  const [cajaEntrega, setCajaEntrega] = useState("");
  const [cajaRetirada, setCajaRetirada] = useState("");
  const [tipoResiduo, setTipoResiduo] = useState("");
  const [destino, setDestino] = useState("");
  const [origen, setOrigen] = useState("");
  const [camionId, setCamionId] = useState(user?.camion_asignado?.id || "");
  const [remolque, setRemolque] = useState("");
  const [coments, setComents] = useState("");

  const hoy = moment();
  const [fechaDay, setFechaDay] = useState(hoy.format("D"));
  const [fechaMonth, setFechaMonth] = useState(hoy.format("MM"));
  const [fechaYear, setFechaYear] = useState(hoy.format("YYYY"));

  const isCreating = !!albaranPendiente || guardando;
  const formularioBloqueado = isCreating && !esEdicion;

  const camionSeleccionado = useMemo(
    () => camiones.find((c) => String(c.id) === String(camionId)),
    [camiones, camionId],
  );

  const capacidades = useMemo(() => {
    const raw = camionSeleccionado?.capacidades_contenedor;
    if (
      (camionSeleccionado?.transporte_contenedores === true ||
        camionSeleccionado?.transporte_contenedores === 1 ||
        camionSeleccionado?.transporte_contenedores === "1") &&
      Array.isArray(raw) &&
      raw.length > 0
    ) {
      return [...new Set(raw.map((v) => Number(v)).filter((v) => v >= 1 && v <= 255))].sort(
        (a, b) => a - b,
      );
    }
    return CAPACIDADES_PRESET;
  }, [camionSeleccionado]);

  useEffect(() => {
    if (!capacidades.includes(capacidad)) {
      setCapacidad(capacidades[0] || CAPACIDADES_PRESET[0]);
    }
  }, [capacidades, capacidad]);

  useEffect(() => {
    if (user && !permitidoContenedor(user.puede_di_contenedor)) {
      history.replace("/ListScreen");
    }
  }, [user, history]);

  useEffect(() => {
    if (!esEdicion || !albaranEdicion) return;
    const obra =
      albaranEdicion.verObra ||
      albaranEdicion.ver_obra ||
      albaranEdicion.direccion;
    const cliente =
      obra?.cliente_porte_id ||
      obra?.cliente?.id ||
      albaranEdicion.cliente_porte_id;
    if (cliente) setClienteId(String(cliente));
    if (obra?.id || albaranEdicion.obra) {
      setDireccionId(String(obra?.id || albaranEdicion.obra));
    }
    if (albaranEdicion.tipo_servicio) setServicio(albaranEdicion.tipo_servicio);
    if (albaranEdicion.capacidad_m3) {
      setCapacidad(Number(albaranEdicion.capacidad_m3));
    }
    setCajaEntrega(albaranEdicion.num_caja_entrega || "");
    setCajaRetirada(albaranEdicion.num_caja_retirada || "");
    const residuoId =
      albaranEdicion.residuo?.id ||
      (typeof albaranEdicion.tipo_residuo === "number"
        ? albaranEdicion.tipo_residuo
        : "");
    if (residuoId) setTipoResiduo(String(residuoId));
    const destinoId =
      albaranEdicion.verts?.id ||
      (typeof albaranEdicion.destino === "number"
        ? albaranEdicion.destino
        : "");
    if (destinoId) setDestino(String(destinoId));
    setOrigen(albaranEdicion.origen || "");
    setRemolque(albaranEdicion.remolque || "");
    setComents(albaranEdicion.comentarios || "");
    const fechaParts = String(albaranEdicion.fecha || "").split("/");
    if (fechaParts.length === 3) {
      setFechaDay(String(Number(fechaParts[0])));
      setFechaMonth(fechaParts[1]);
      setFechaYear(fechaParts[2]);
    }
  }, [esEdicion, albaranEdicion]);

  useEffect(() => {
    if (!user?.id || !permitidoContenedor(user.puede_di_contenedor)) return;

    const cargar = async () => {
      let datos = null;
      if (isOnline) {
        try {
          datos = await fetchClientesContenedor(user.id);
          localStorage.setItem(cacheKey(user.id), JSON.stringify(datos));
        } catch (error) {
          if (error?.response?.status === 403) {
            localStorage.removeItem(cacheKey(user.id));
            history.replace("/ListScreen");
            return;
          }
        }
      }
      if (!datos) {
        try {
          datos = JSON.parse(localStorage.getItem(cacheKey(user.id)));
        } catch (_error) {}
      }
      setClientes(datos?.clientes || []);
      setVertederos(datos?.vertederos || []);
      setLoading(false);
    };
    cargar();

    if (isOnline && camiones.length === 0) {
      fetchCamiones({ id: user.operador })
        .then((res) => Array.isArray(res) && dispatch(setCamiones(res)))
        .catch(() => {});
    }
    if (isOnline && tipos.length === 0) {
      fetchTipos()
        .then((res) => Array.isArray(res) && dispatch(setTipos(res)))
        .catch(() => {});
    }
    // Solo al entrar en la pantalla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const cliente = useMemo(
    () => clientes.find((c) => String(c.id) === String(clienteId)),
    [clientes, clienteId],
  );
  const direcciones = useMemo(() => cliente?.direcciones || [], [cliente]);
  const esOtro = direccionId === "otro";
  const direccion = esOtro
    ? null
    : direcciones.find((d) => String(d.id) === String(direccionId));
  const operadorDestino =
    (esOtro ? cliente?.operador : direccion?.operador) ||
    albaranEdicion?.operador ||
    albaranEdicion?.verObra?.operador ||
    albaranEdicion?.ver_obra?.operador;

  // Públicos + privados del operador dueño de la dirección (o del cliente, si es «Otro»).
  const destinos = useMemo(
    () =>
      vertederos.filter(
        (v) =>
          !v.is_private ||
          (operadorDestino && String(v.operador_id) === String(operadorDestino)),
      ),
    [vertederos, operadorDestino],
  );

  useEffect(() => {
    if (esEdicion) return;
    if (clientes.length === 1 && !clienteId) {
      setClienteId(String(clientes[0].id));
    }
  }, [clientes, clienteId, esEdicion]);

  useEffect(() => {
    if (esEdicion || direccionId) return;
    if (direcciones.length === 1) {
      setDireccionId(String(direcciones[0].id));
    } else if (cliente && direcciones.length === 0) {
      setDireccionId("otro");
    }
  }, [direcciones, direccionId, cliente, esEdicion]);

  useEffect(() => {
    if (esEdicion) return;
    if (direccion?.vertedero) {
      setDestino(String(direccion.vertedero));
    }
  }, [direccion, esEdicion]);

  useEffect(() => {
    if (esEdicion && albaranEdicion?.camion && camiones.length > 0) {
      const porMatricula = camiones.find(
        (c) => c.matricula === albaranEdicion.camion,
      );
      if (porMatricula) {
        setCamionId(porMatricula.id);
        return;
      }
    }
    const camion = camiones.find((c) => String(c.id) === String(camionId));
    if (camion) {
      if (!esEdicion) setRemolque(camion.remolque || "");
    } else if (!camionId && camiones.length > 0) {
      setCamionId(camiones[0].id);
    }
  }, [camiones, camionId, esEdicion, albaranEdicion]);

  const handleClienteChange = (e) => {
    if (esEdicion) return;
    setClienteId(e.target.value);
    setDireccionId("");
    setDireccionLibre("");
  };

  const payloadDatos = (camion) => {
    const esLlevada = servicio === "llevada";
    const requiereResiduo = !esLlevada;
    const conEntrega = servicio !== "retirada";
    const conRetirada = !esLlevada;
    return {
      conductor_id: user.id,
      camion: camion.matricula,
      remolque: remolque || null,
      tipo_servicio: servicio,
      capacidad_m3: capacidad,
      num_caja_entrega: conEntrega ? cajaEntrega.trim() || null : null,
      num_caja_retirada: conRetirada ? cajaRetirada.trim() || null : null,
      tipo_residuo: requiereResiduo ? Number(tipoResiduo) : null,
      comentarios: coments || null,
      ...(esLlevada
        ? { origen: origen.trim() }
        : { destino: Number(destino) }),
    };
  };

  const validarFormulario = (camion, fecha) => {
    const esLlevada = servicio === "llevada";
    if (!esEdicion) {
      if (!cliente) return "Selecciona el cliente.";
      if (esOtro && !direccionLibre.trim()) return "Escribe la dirección.";
      if (!esOtro && !direccion) return "Selecciona la dirección.";
    }
    if (!servicio) return "Selecciona el tipo de servicio.";
    if (!esLlevada && !tipoResiduo) return "Selecciona el tipo de residuo.";
    if (!esLlevada && !destino) return "Selecciona el destino.";
    if (!camion) return "Selecciona la matrícula.";
    if (!esEdicion && !fecha.isValid()) return "La fecha introducida no es válida.";
    return null;
  };

  const handleSubmit = async () => {
    const camion = camiones.find((c) => String(c.id) === String(camionId));
    const fecha = moment(
      `${fechaDay}/${fechaMonth}/${fechaYear}`,
      "D/MM/YYYY",
      true,
    );
    const error = validarFormulario(camion, fecha);
    if (error) return alert(error);

    if (esEdicion) {
      if (!isOnline) {
        return alert("Necesitas conexión para completar el DI.");
      }
      setGuardando(true);
      try {
        await completarDiContenedor(albaranEdicion.id, payloadDatos(camion));
        history.replace({
          pathname: "/albaranCreated",
          albaran: { id: albaranEdicion.id },
        });
      } catch (err) {
        const errores = err?.response?.data?.errors;
        const msg =
          err?.response?.data?.message ||
          (errores ? Object.values(errores).flat().join(" ") : null) ||
          "No se ha podido guardar el DI.";
        alert(msg);
      } finally {
        setGuardando(false);
      }
      return;
    }

    dispatch(
      setAlbaranPendiente({
        tipo: "contenedor",
        hora: moment().format("HH:mm"),
        fecha: fecha.format("DD/MM/YYYY"),
        ...(esOtro
          ? { cliente_id: cliente.id, direccion_libre: direccionLibre.trim() }
          : { obra: direccion.id }),
        ...(servicio === "llevada" ? { ref: `C${Date.now()}` } : {}),
        ...payloadDatos(camion),
      }),
    );
    history.push("/ListScreen");
  };

  return (
    <div>
      <div className="header">
        <Header />
      </div>
      <form>
        <div className="containerPrincipalDO">
          <div className="containerPopupDO">
            {isLoading ? (
              <p className="SubTitulo">Cargando…</p>
            ) : clientes.length === 0 ? (
              <p className="SubTitulo">
                No tienes direcciones de contenedores asignadas. Pide a tu
                empresa que te asigne alguna.
              </p>
            ) : (
              <>
                <div>
                  <p className="SubTitulo">Cliente</p>
                  <select
                    className="selectColor selectSizer"
                    value={clienteId}
                    onChange={handleClienteChange}
                    disabled={formularioBloqueado || esEdicion}
                  >
                    <option value="">Selecciona el cliente</option>
                    {clientes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.razon_social}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="SubTitulo">
                    {servicio === "llevada" ? "Destino" : "Dirección"}
                  </p>
                  <select
                    className="selectColor selectSizer"
                    value={direccionId}
                    onChange={(e) => setDireccionId(e.target.value)}
                    disabled={isCreating || !cliente}
                  >
                    <option value="">Selecciona la dirección</option>
                    {direcciones.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.nombre} — {d.direccion}
                      </option>
                    ))}
                    <option value="otro">Otro</option>
                  </select>
                  {esOtro && (
                    <input
                      className="inputs"
                      placeholder="Escribe la dirección"
                      value={direccionLibre}
                      onChange={(e) => setDireccionLibre(e.target.value)}
                      maxLength={255}
                      disabled={formularioBloqueado || esEdicion}
                    />
                  )}
                </div>
                <div>
                  <p className="SubTitulo">Tipo de servicio</p>
                  <div className="contenedor-opciones">
                    {SERVICIOS.map((s) => (
                      <button
                        type="button"
                        key={s.value}
                        className={
                          "contenedor-opcion" +
                          (servicio === s.value ? " is-activa" : "")
                        }
                        onClick={() => setServicio(s.value)}
                        disabled={formularioBloqueado}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="SubTitulo">
                    Capacidad del contenedor (m<sup>3</sup>)
                  </p>
                  <div className="contenedor-opciones">
                    {capacidades.map((c) => (
                      <button
                        type="button"
                        key={c}
                        className={
                          "contenedor-opcion" +
                          (capacidad === c ? " is-activa" : "")
                        }
                        onClick={() => setCapacidad(c)}
                        disabled={formularioBloqueado}
                      >
                        {c} m<sup>3</sup>
                      </button>
                    ))}
                  </div>
                </div>
                {servicio && servicio !== "retirada" && (
                  <div>
                    <p className="SubTitulo">Nº de caja entrega</p>
                    <input
                      className="inputs"
                      placeholder="Opcional"
                      value={cajaEntrega}
                      onChange={(e) => setCajaEntrega(e.target.value)}
                      maxLength={50}
                      disabled={formularioBloqueado}
                    />
                  </div>
                )}
                {servicio && servicio !== "llevada" && (
                  <div>
                    <p className="SubTitulo">Nº de caja retirada</p>
                    <input
                      className="inputs"
                      placeholder="Opcional"
                      value={cajaRetirada}
                      onChange={(e) => setCajaRetirada(e.target.value)}
                      maxLength={50}
                      disabled={formularioBloqueado}
                    />
                  </div>
                )}
                {servicio !== "llevada" && (
                  <div>
                    <p className="SubTitulo">Tipo de residuo</p>
                    <select
                      className="selectColor selectSizer"
                      value={tipoResiduo}
                      onChange={(e) => setTipoResiduo(e.target.value)}
                      disabled={formularioBloqueado}
                    >
                      <option value="">Selecciona el residuo</option>
                      {tipos.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.nombre} - {t.codigo_ler}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {servicio === "llevada" ? (
                  <div>
                    <p className="SubTitulo">Origen (opcional)</p>
                    <input
                      className="inputs"
                      placeholder="Origen (opcional)"
                      value={origen}
                      onChange={(e) => setOrigen(e.target.value)}
                      maxLength={255}
                      disabled={formularioBloqueado}
                    />
                  </div>
                ) : (
                  <div>
                    <p className="SubTitulo">Destino</p>
                    <select
                      className="selectColor selectSizer"
                      value={destino}
                      onChange={(e) => setDestino(e.target.value)}
                      disabled={formularioBloqueado}
                    >
                      <option value="">Selecciona el destino</option>
                      {destinos.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <p className="SubTitulo">Nombre de Conductor</p>
                  <input className="inputs" readOnly value={user?.nombre || ""} />
                </div>
                <div>
                  <p className="SubTitulo">Matricula</p>
                  <select
                    className="selectColor selectSizer"
                    value={camionId}
                    onChange={(e) => setCamionId(Number(e.target.value))}
                    disabled={formularioBloqueado}
                  >
                    {camiones.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.matricula}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="SubTitulo">Matricula Remolque</p>
                  <input
                    className="inputs"
                    placeholder="Matrícula remolque"
                    value={remolque}
                    onChange={(e) => setRemolque(e.target.value)}
                    disabled={formularioBloqueado}
                  />
                </div>
                <div>
                  <p className="SubTitulo">Comentarios</p>
                  <input
                    placeholder="Comentarios"
                    className="inputs comentario"
                    value={coments}
                    onChange={(e) => setComents(e.target.value)}
                    disabled={formularioBloqueado}
                  />
                </div>
                <div>
                  <div className="fechaIcon">
                    <FiCalendar id="calendarIcon" />
                    <p className="SubTitulo">Fecha</p>
                  </div>
                  <div className="fecha-input">
                    <input
                      placeholder="DD"
                      className="inputs fecha-dd"
                      value={fechaDay}
                      onChange={(e) => setFechaDay(e.target.value)}
                      disabled={formularioBloqueado || esEdicion}
                    />
                    <input
                      placeholder="MM"
                      className="inputs fecha-mm"
                      value={fechaMonth}
                      onChange={(e) => setFechaMonth(e.target.value)}
                      disabled={formularioBloqueado || esEdicion}
                    />
                    <input
                      placeholder="YYYY"
                      className="inputs fecha-yy"
                      value={fechaYear}
                      onChange={(e) => setFechaYear(e.target.value)}
                      disabled={formularioBloqueado || esEdicion}
                    />
                  </div>
                </div>
                {!isOnline && !esEdicion && (
                  <div className="aviso--offline">
                    Estás sin conexión a internet, el DI se creará cuando tengas
                    conexión disponible
                  </div>
                )}
                {esEdicion && (
                  <div className="aviso--offline">
                    Completa residuo, destino y el resto de datos antes de generar
                    el PDF.
                  </div>
                )}
                <div className="btn">
                  <button
                    type="button"
                    className="txtBtnCreaAlbaran"
                    onClick={handleSubmit}
                    disabled={formularioBloqueado}
                  >
                    {guardando
                      ? "Guardando…"
                      : esEdicion
                        ? "Guardar datos"
                        : "Crear DI"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default DetallesContenedor;
